import { FunctionComponent, useState } from 'react';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import cn from 'classnames';
import { useBatchApi } from '../hooks';
import {
  BatchApiStatusHandler,
  Button,
  Section,
  UsStateSelectField,
} from '../components';
import { Person, Region, SubscriptionPlanEnum } from '../types';
import { useGlobalContext } from '../contexts/globalContext';
import { fetchApi } from '../utils/fetchApi';
import { ApiStatus, IApiData } from '../utils/types';
import { USStateName } from '../constants';
import { OnboardingBusinessProfileSuccess } from '../views/OnboardingBusinessProfileSuccess';
import requirementCompleteImage from '../assets/requirement_complete.png';
import { FormError } from '../components/forms/FormError';
import { withRedirect } from '../utils/routing';
import { useUser } from '../hooks/useUser';

interface BasicPlanIFormValues {
  hq_state?: string;
}

type SetupOneLocationProps = {
  locationName: string;
  hasMultipleFounders: boolean;
  hasFormError?: boolean;
};

const SetupOneLocation: FunctionComponent<SetupOneLocationProps> = ({
  locationName,
  hasMultipleFounders,
  hasFormError,
}) => {
  return (
    <div
      className={cn(
        'text-medium mt-16 flex flex-col items-center justify-center space-y-8'
      )}
    >
      <img
        src={requirementCompleteImage}
        alt="Requirement complete"
        className="ml-12 h-44 w-56"
      />
      <div className="text-4xl font-extrabold leading-10 text-gray-700">
        Looks like you have {!hasMultipleFounders ? 'a founder' : 'founders'} in{' '}
        {locationName}!
      </div>

      <div className="my-8 flex w-96 flex-col items-center">
        <p className="flex flex-col items-center">
          <label className="text-center">
            We&apos;ve added a few tasks to your account so you can use Mosey to
            register locally.
          </label>
          <Button className="mt-8 w-24" type="submit" size="large">
            Continue
          </Button>
        </p>
        {hasFormError && <FormError />}
      </div>
    </div>
  );
};

type SetupMultipleLocationsProps = {
  locationNames: string[];
  hasMultipleFounders: boolean;
  errors?: any;
  hasFormError?: boolean;
};

const SetupMultipleLocations: FunctionComponent<
  SetupMultipleLocationsProps
> = ({ locationNames, hasMultipleFounders, errors, hasFormError }) => {
  return (
    <div
      className={cn(
        'text-medium mt-16 flex w-96 flex-col justify-center space-y-8'
      )}
    >
      <>
        <div className="w-96 text-3xl font-extrabold leading-10 text-gray-700">
          Looks like you have {!hasMultipleFounders ? 'a founder' : 'founders'}{' '}
          in multiple states
        </div>
      </>
      <span className="flex flex-col">
        <UsStateSelectField
          name="hq_state"
          label="We'll add tasks to your account so you can use Mosey to register locally. First, choose where your business is headquartered:"
          description="For distributed teams this is where the highest concentration of founders live or where the CEO lives."
          limitedStateOptions={locationNames}
          reactFormConfig={{ required: 'This is required' }}
          error={errors.hq_state}
        />
        <Button className="w-24" type="submit" size="large">
          Continue
        </Button>
        {hasFormError && <FormError />}
      </span>
    </div>
  );
};

type BasicPlanSetupProps = {
  peopleData: Person[];
  regions: Region[];
};

const BasicPlanSetup: FunctionComponent<BasicPlanSetupProps> = ({
  peopleData,
  regions,
}) => {
  const [hasFormError, setFormError] = useState<boolean>(false);
  const { setGlobalState } = useGlobalContext();
  const navigate = useNavigate();

  const peopleLocationsUsSet: Set<string> = new Set();
  peopleData
    .filter((p) => p.is_officer || p.is_owner || p.is_member)
    .forEach((person) => {
      const stateNames: string[] = Object.values(USStateName);
      if (person.address?.state && stateNames.includes(person.address.state)) {
        peopleLocationsUsSet.add(person.address.state);
      }
    });
  const peopleLocationsUs: string[] = Array.from(peopleLocationsUsSet);

  const formMethods = useForm({
    defaultValues: {
      hq_state:
        peopleLocationsUs.length === 1
          ? regions
              .filter(
                (r) =>
                  r.name.toLowerCase() === peopleLocationsUs[0].toLowerCase()
              )
              .map((r) => r.code)[0]
          : undefined,
    },
  });
  const {
    handleSubmit,
    formState: { errors },
  } = formMethods;

  const onSubmit: SubmitHandler<BasicPlanIFormValues> = (data) => {
    setFormError(false);
    let regionCode: string;
    if (data.hq_state) {
      regionCode = data.hq_state;
    } else {
      setFormError(true);
      return;
    }

    // Make an API call to create LegalEntityRegion and related requirements
    fetchApi({
      url: `/api/onboarding/basic`,
      method: 'POST',
      body: { hq_region: regionCode },
    }).then(({ status, data: responseData }: IApiData) => {
      if (status === ApiStatus.Error) {
        setFormError(true);
        return;
      }

      setGlobalState((oldState) => {
        const newState = { ...oldState };
        newState.user.legal_entity.regions = [
          ...newState.user.legal_entity.regions,
          responseData,
        ];
        newState.user.legal_entity.hq_region = responseData;

        return newState;
      });
      navigate('/home', { replace: true });
    });
  };

  return (
    <>
      {peopleLocationsUs.length === 0 ? (
        <>
          {/* All officers and owners have international addresses */}
          <OnboardingBusinessProfileSuccess
            onClick={() =>
              setGlobalState((prevState) => ({
                ...prevState,
                hasCheckedBasicPlanOnboarding: true,
              }))
            }
          />
        </>
      ) : (
        <Section>
          <div
            className={cn(
              'text-medium mt-16 flex flex-col items-center justify-center space-y-8'
            )}
          >
            <FormProvider {...formMethods}>
              <form onSubmit={handleSubmit(onSubmit)}>
                {peopleLocationsUs.length === 1 ? (
                  <SetupOneLocation
                    locationName={peopleLocationsUs[0]}
                    hasMultipleFounders={peopleData.length > 1}
                    hasFormError={hasFormError}
                  />
                ) : (
                  <SetupMultipleLocations
                    locationNames={peopleLocationsUs}
                    hasMultipleFounders={peopleData.length > 1}
                    errors={errors}
                    hasFormError={hasFormError}
                  />
                )}
              </form>
            </FormProvider>
          </div>
        </Section>
      )}
    </>
  );
};

export const BasicPlanSetupView: FunctionComponent = () => {
  const batchResponse = useBatchApi([
    {
      url: `/api/legal_entity/people`,
      method: 'GET',
    },
    {
      url: '/api/regions',
      method: 'GET',
    },
  ]);

  const componentPropsFn = ([
    personResponse,
    regions,
  ]: any[]): BasicPlanSetupProps => {
    return {
      peopleData: personResponse,
      regions,
    };
  };

  return (
    <BatchApiStatusHandler
      batchResponse={batchResponse}
      component={BasicPlanSetup}
      componentProps={componentPropsFn}
    />
  );
};

export const ConditionalBasicPlanSetupView = withRedirect(
  BasicPlanSetupView,
  () => {
    const user = useUser();
    return user.legal_entity.subscription_plan === SubscriptionPlanEnum.Basic;
  },
  '/'
);
